import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  editPersona,
  fetchMentorById,
  fetchMentorProfileBySlug,
  fetchMentorPsychometricsById,
  fetchPersonaById,
  fetchPrompts,
  generateImageByContext,
  updatePsychometrics,
} from '../../api/personas'
import { Profile } from '../../types/persona'

export const usePersonaById = (
  id: string,
  isAuthenticated?: boolean,
  refetch: boolean = false,
) =>
  useQuery<{
    data: {
      profile: {
        profile: Profile
      }
    }
  }>({
    queryKey: ['getPersonaById', id],
    queryFn: () => fetchPersonaById(id),
    enabled: !!id && isAuthenticated,
    refetchInterval: ({ state }) => {
      const status = state?.data?.data?.profile?.profile?.status

      if (
        status === 'ReadyToUse' ||
        status === 'Completed' ||
        status === 'Failed' ||
        status === 'WaitingForApproval' ||
        !refetch
      )
        return false
      return 10000
    },
  })

export const usePersonaBySlug = (
  slug: string,
  isAuthenticated?: boolean,
  refetch: boolean = false,
) =>
  useQuery({
    queryKey: ['getPersonaBySlug', slug],
    queryFn: () => fetchMentorProfileBySlug(slug),
    enabled: !!slug && isAuthenticated,
    refetchInterval: ({ state }) => {
      const status = state?.data?.data?.status
      if (
        status === 'Failed' ||
        status === 'Completed' ||
        status === 'ReadyToUse' ||
        status === 'WaitingForApproval' ||
        state.error ||
        !refetch
      )
        return false
      return 3000
    },
  })

export const useMentorPsychometricsById = (
  id: string,
  isAuthenticated: boolean,
) =>
  useQuery({
    queryKey: ['getMentorPsychometricsById', id],
    queryFn: () => fetchMentorPsychometricsById(id),
    enabled: !!id && isAuthenticated,
  })

export const useMentorById = (id: string) =>
  useQuery({
    queryKey: ['getAvatarById', id],
    queryFn: () => fetchMentorById(id),
    enabled: !!id,
  })

export const usePrompts = (id: string, isAuthenticated: boolean) =>
  useQuery({
    queryKey: ['getPrompts', id],
    queryFn: () => fetchPrompts(id),
    enabled: !!id && isAuthenticated,
  })

export const useUpdatePersona = () => useMutation({ mutationFn: editPersona })

export const useUpdatePsychometrics = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updatePsychometrics,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['getMentorPsychometricsById'],
      })
    },
  })
}

export const useGenerateImageByContext = () =>
  useMutation({ mutationFn: generateImageByContext })
